import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import '../css/layout.styl'
import PaymentMethod from './PaymentMethod'
import PaymentMethodSidebar from '../components/PaymentMethodSidebar'
import PaymentActions from './PaymentActions'
import { getUrlAssets } from '../constants'
import PaymentMethodConfirmation from '../components/PaymentMethodConfirmation'
import SuccessAnimation from '../components/SuccessAnimation'
import Modal from 'react-modal';
import { selectLoading as selectAuthLoading } from '../app/reducers/auth'
import { selectLoading as selectSessionLoading } from '../app/reducers/session'
import { selectLoading as selectOperationLoading, selectOperation, selectCaptureConfigurations } from '../app/reducers/operation'
import { selectLoading as selectPaymentMethodLoading, selectLoadingTransaction, 
  selectId as selectPaymentMethodId,
  selectResponseWithAnimation, selectNextAction,
  selectGooglePayStep, selectGooglePayPendingSwitch,
  setGooglePayPendingSwitch, clearGooglePayPendingSwitch, resetGooglePay
} from '../app/reducers/paymentMethod'
import { selectList } from '../app/reducers/methodsList'
import { selectLoading as selectAuthenticationLoading, selectLoadingAuthorization } from '../app/reducers/authentication'
import { selectSettings } from '../app/reducers/flexConfiguration';
import { setOperationChange } from '../app/reducers/paymentMethodChange';
import { selectLanguage, setLanguage, selectLanguages, selectLanguageMode } from '../app/reducers/language';
import { useTranslation } from '../hooks/useTranslation';
import { resolveAcquirerLogo, resolveAcquirerLogoAlt } from '../utils/acquirerLogo';

const MERCHANT_SUBTITLE_DEFAULT_KEY = 'payment_header.subtitle_default';

const getInitialResolution = () => {
  const w = window.innerWidth;
  if (w >= 1536) return '2xl';
  if (w >= 1280) return 'xl';
  if (w >= 1024) return 'lg';
  if (w >= 768) return 'md';
  if (w >= 640) return 'sm';
  return 'xs';
};

export default function PaymentUI({responseCallback, trackingCallback, onErrorCallback}){
  const [resolution, setResolution] = useState(getInitialResolution);
  const [collapsed, setCollapsed] = useState(true);
  const containerRef = useRef(null);
  const modalContainerRef = useRef(null);
  const collapsedRef = useRef(true);
  collapsedRef.current = collapsed;
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      if (!collapsedRef.current) return; // skip resize when sidebar is expanded to prevent infinite loop
      for (let entry of entries) {
        let w = entry.contentRect.width;
        if(w>=1536){
          setResolution('2xl');
        }else if(w>=1280){
          setResolution('xl');
        }else if(w>=1024){
          setResolution('lg');
        }else if(w>=768){
          setResolution('md');
        }else if(w>=640){
          setResolution('sm');
        }else{
          setResolution('xs');
        }
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect(); // clean up 
  }, []);
  const dispatch = useDispatch();
  const operationData = useSelector(selectOperation);
  const captureConfigurations = useSelector(selectCaptureConfigurations);
  const authLoading = useSelector(selectAuthLoading);
  const sessionLoading = useSelector(selectSessionLoading);
  const operationLoading = useSelector(selectOperationLoading);
  const paymentMethodLoading = useSelector(selectPaymentMethodLoading);
  const transactionLoading = useSelector(selectLoadingTransaction);
  const authenticationLoading = useSelector(selectAuthenticationLoading);
  const authorizationLoading = useSelector(selectLoadingAuthorization);
  let isPreloading = authLoading || sessionLoading ;
  let isLoading = authLoading ||
                    sessionLoading ||
                    operationLoading ||
                    paymentMethodLoading ||
                    transactionLoading ||
                    authenticationLoading ||
                    authorizationLoading;
  const methodsList = useSelector(selectList);
  const methodId = useSelector(selectPaymentMethodId);
  const checkoutReady = methodsList.length > 0;
  const isOperationLoading = checkoutReady && isLoading && !isPreloading;
  const nextAction = useSelector(selectNextAction);
  const googlePayStep = useSelector(selectGooglePayStep);
  const googlePayPendingSwitch = useSelector(selectGooglePayPendingSwitch);

  const doChangeMethod = (method, operation) => {
    setCollapsed(true);
    console.log({methodChoosed:method, operation:operation});
    dispatch(setOperationChange({
      operation: operation,
      paymentMethod: method
    }));
  };

  const chooseMethod = (method, operation) => {
    const inGooglePayFlow = (googlePayStep === 'loading' || googlePayStep === 'installments');
    if (inGooglePayFlow && method) {
      dispatch(setGooglePayPendingSwitch({ method, operation }));
      return;
    }
    doChangeMethod(method, operation);
  };

  const resolveGooglePaySwitch = (confirm) => {
    const pending = googlePayPendingSwitch;
    dispatch(clearGooglePayPendingSwitch());
    if (confirm === 'OK' && pending) {
      dispatch(resetGooglePay());
      doChangeMethod(pending.method, pending.operation);
    }
  };
  const responseWithAnimation = useSelector(selectResponseWithAnimation);
  const configSettings = useSelector(selectSettings);
  const language = useSelector(selectLanguage);
  const availableLanguages = useSelector(selectLanguages);
  const languageMode = useSelector(selectLanguageMode);
  const { t, tMixed } = useTranslation();
  const defaultAcquirerLogo = getUrlAssets() + "/payme-logo.svg";
  const configuredAcquirerLogo = resolveAcquirerLogo({
    localSettings: configSettings,
    captureConfigurations
  });
  const hasAcquirerLogo = Boolean(configuredAcquirerLogo);
  const [footerLogoSrcOverride, setFooterLogoSrcOverride] = useState(null);
  const [footerLogoHidden, setFooterLogoHidden] = useState(false);
  const effectiveFooterLogoSrc = footerLogoSrcOverride || configuredAcquirerLogo;
  const altOverride = resolveAcquirerLogoAlt({ localSettings: configSettings, captureConfigurations });
  const translatedAlt = t('payment_method.acquirer_logo_alt');
  const acquirerLogoAlt = altOverride
    ? tMixed(altOverride)
    : (translatedAlt === 'payment_method.acquirer_logo_alt' ? 'Logo del adquirente' : translatedAlt);
  useEffect(() => {
    setFooterLogoSrcOverride(null);
    setFooterLogoHidden(false);
  }, [configuredAcquirerLogo]);
  const handleAcquirerLogoError = () => {
    console.error({ acquirerLogoLoadError: effectiveFooterLogoSrc });
    if (effectiveFooterLogoSrc !== defaultAcquirerLogo) {
      setFooterLogoSrcOverride(defaultAcquirerLogo);
    } else {
      setFooterLogoHidden(true);
    }
  };
  const hideResultScreen = () => {
    return (!configSettings.display_result_screen && nextAction && nextAction.type=="execute_response_callback" && responseWithAnimation);
  }
  const showPaymentResult = () => {
    return (configSettings.display_result_screen === true && nextAction && nextAction.type=="execute_response_callback" && nextAction.withDelay===true);
  }

  const merchantLogo = configSettings.merchant_logo || captureConfigurations.merchant_logo;
  const merchantName = configSettings.merchant_name || captureConfigurations.merchant_name;

  const configuredSubtitle = configSettings.merchant_subtitle || captureConfigurations.merchant_subtitle;
  const defaultSubtitle = t(MERCHANT_SUBTITLE_DEFAULT_KEY);
  const hasDefaultSubtitle = defaultSubtitle !== MERCHANT_SUBTITLE_DEFAULT_KEY;
  const merchantSubtitle = configuredSubtitle || (hasDefaultSubtitle ? defaultSubtitle : null);

  const showLanguageSelector = languageMode !== 'single' && availableLanguages.length > 0;
  const showCloseButton = Boolean(configSettings.show_close_button);
  const showHeader = Boolean(merchantLogo || showLanguageSelector || showCloseButton);

  const handleCloseCart = () => onErrorCallback({ "message": "Cerro el carrito" });

  return(
    <div className={"payment-gateway-ui"} ref={containerRef}>
      <div className={"payment-gateway-modal-container"} ref={modalContainerRef}></div>
      {false && <Modal
        isOpen={!isLoading}
        contentLabel="Example Modal"
        parentSelector={() => modalContainerRef.current}
      >
        <div>I am a modal</div>
      </Modal>}
      <div className={"loading-overlay "+(isPreloading ? 'is-loading' : 'is-not-loading')}>
        <div className={"loading-detail clear-loader"}>
          <div className={"loading-icon-clear"}></div>
        </div>
      </div>
      <div className={"loading-overlay "+(isOperationLoading ? 'is-loading' : 'is-not-loading')}>
        <div className={"loading-detail clear-loader"}>
          <div className={"operation-spinner"}></div>
        </div>
      </div>
      <PaymentMethodConfirmation />
      <SuccessAnimation />
      <div className={"loading-overlay "+(googlePayPendingSwitch ? ' is-loading' : ' is-not-loading')}>
        {googlePayPendingSwitch && <div className={"loading-detail"}>
          <div className={"modal-title"}>{t('modal.cancel_method')}</div>
          <div className={"modal-message"}>{t('modal.confirm_change_method')}</div>
          <div className={"modal-buttons"}>
            <button className={"modal-button"} onClick={()=>resolveGooglePaySwitch("NO")}>{t('common.no')}</button>
            <button className={"modal-button modal-button-highlighted"} onClick={()=>resolveGooglePaySwitch("OK")}>{t('common.yes')}</button>
          </div>
        </div>}
      </div>
      <div className={"payment-gateway-container payui-resolution-"+resolution+(collapsed?"":" expanded")+(hideResultScreen()?" hide-result-screen":"")+(configSettings.show_border===false?" no-border":"")+(isPreloading?"":" content-ready")} >
        {showHeader && <div className={"payment-header"}>
          {merchantLogo && <img src={merchantLogo} className={"merchant-logo"} alt="Logo del comercio" />}

          {merchantName && <div className={"payment-header-info"}>
            <span className={"merchant-name"}>{merchantName}</span>
            {merchantSubtitle && <span className={"merchant-subtitle"}>{merchantSubtitle}</span>}
          </div>}

          <div className={"payment-header-actions"}>
            {showLanguageSelector && <div className={"language-selector"}>
              <span className={"language-selector-indicator"} style={{transform: `translateX(${availableLanguages.indexOf(language) * 100}%)`}} />
              {availableLanguages.map((lang) => (
                <span
                  key={lang}
                  className={'language-option' + (language === lang ? ' active' : '')}
                  onClick={() => dispatch(setLanguage(lang))}
                >
                  {lang.toUpperCase()}
                </span>
              ))}
            </div>}

            {showCloseButton && <div
              className={"close-btn"}
              role="button"
              tabIndex={0}
              onClick={handleCloseCart}
              onKeyDown={(e)=>{if(e.key==='Enter'||e.key===' ')handleCloseCart()}}
            >
              <span className={"close-btn-x"}>✕</span>
            </div>}
          </div>
        </div>}
        {/* select-payment-message movido a PaymentMethodSidebar */}
        <div className={"payment-methods-container"}>
          {methodsList.length > 1 && <PaymentMethodSidebar methodsList={methodsList} methodId={methodId} chooseMethod={chooseMethod} resolution={resolution} collapsed={collapsed} setCollapsed={setCollapsed} />}
          {methodsList.map((value,pos) => (<PaymentMethod key={pos} method={value} methodChoosed={value.id==methodId} showResult={showPaymentResult()} />))}
          {/* --- Antes: los botones se renderizaban dentro de cada PaymentMethod y el collapse btn estaba aquí ---
          {resolution=="xs" && methodsList.length > 1 && 
            <div className={"payment-method-wrapper collapse-btn"}>
              <div className={"payment-choose-btn choose-btn-"+(1+methodsList.length)} onClick={()=>{setCollapsed(!collapsed)}}>
                <img src={getUrlAssets()+(collapsed?"/sandwich.png":"/close.png")}
                  className={"sm-nav-payment-method-icon"}/>
              </div>
            </div>}
          */}
        </div>
        <PaymentActions chooseMethod={chooseMethod} responseCallback={responseCallback} showResult={showPaymentResult()} />
        {(captureConfigurations.show_operation_number || (hasAcquirerLogo && !footerLogoHidden) || configSettings.show_secure_payment || captureConfigurations.show_secure_payment) && <div className={"payment-footer"}>
          {captureConfigurations.show_operation_number && <div className={"payment-operation-number"}>{operationData ? tMixed(captureConfigurations.show_operation_number_prefix)+operationData.merchant_operation_number : ""}</div>}
          {hasAcquirerLogo && !footerLogoHidden && <img src={effectiveFooterLogoSrc} onError={handleAcquirerLogoError} className={"payment-footer-logo"} alt={acquirerLogoAlt} />}
          {(configSettings.show_secure_payment || captureConfigurations.show_secure_payment) && <div className={"payment-secure"}>
            <img src={getUrlAssets()+"/secure_payment.svg"} className={"payment-secure-icon"} alt="" aria-hidden="true" />
            <span className={"payment-secure-text"}>{t('payment_footer.secure_text')}</span>
          </div>}
        </div>}
      </div>
    </div>
  );
}